<template>
  <div v-if="enabled" class="workflow-agent-container" :class="{ 'is-mobile': mobile }">
  <div v-if="inline" class="workflow-agent-inline" :class="{ 'is-mobile': mobile }" aria-live="polite">
    <span>{{ $t("workflowChat.agentName") }} · {{ $t("workflowChatAgent.readOnly") }}</span>
    <div v-if="mobile && canRequest" class="mobile-agent-actions">
      <button v-for="capability in capabilities" :key="capability" type="button" :disabled="busy" @click="submit(capability)">{{ $t('workflowChatAgent.' + capability) }}</button>
    </div>
    <span v-for="task in tasks.slice(0, 1)" :key="task.taskId">
      {{ $t("workflowChatAgent.status." + task.status) }}
      <button v-if="canCancel(task)" type="button" :disabled="busy" @click="cancel(task)">{{ $t("workflowChatAgent.cancel") }}</button>
      <span v-if="task.errorCode">{{ $t("workflowChatAgent.failedHint") }}</span>
    </span>
    <span v-if="error" role="alert">{{ error }}</span>
  </div>
  <details
    v-else
    class="workflow-agent"
    @toggle="expanded = $event.target.open"
  >
    <summary>{{ $t("workflowChatAgent.title") }}</summary>
    <div v-if="expanded" class="workflow-agent__body">
      <div class="workflow-agent__actions">
        <button
          v-for="capability in capabilities"
          :key="capability"
          type="button"
          :class="{ primary: capability === 'progress' }"
          :disabled="busy"
          @click="submit(capability)"
        >
          {{ $t("workflowChatAgent." + capability) }}
        </button>
      </div>
      <p class="workflow-agent__hint">
        {{ $t("workflowChatAgent.rangeHint") }}
      </p>
      <p v-if="error" role="alert">{{ error }}</p>
      <div class="workflow-agent__tasks" aria-live="polite">
        <article v-for="task in tasks.slice(0, 8)" :key="task.taskId">
          <header>
            <strong>{{ $t("workflowChatAgent." + task.capability) }}</strong>
            <span>{{ $t("workflowChatAgent.status." + task.status) }}</span>
            <button
              v-if="canCancel(task)"
              type="button"
              :disabled="busy"
              @click="cancel(task)"
            >
              {{ $t("workflowChatAgent.cancel") }}
            </button>
          </header>
          <small>{{ task.fromSequence }}–{{ task.toSequence }}</small>
          <template v-if="task.result">
            <div
              v-for="(point, index) in task.result.points"
              :key="index"
              class="workflow-agent__point"
            >
              <div v-html="markdown(point.text)"></div>
              <details
                v-for="sourceId in point.sourceIds"
                :key="sourceId"
                class="workflow-agent__evidence"
              >
                <summary>{{ evidenceLabel(sourceId) }}</summary>
                <blockquote>{{ evidence(task, sourceId) }}</blockquote>
              </details>
            </div>
            <small>{{ task.result.generatedAt }}</small>
          </template>
          <p v-if="task.errorCode">{{ $t("workflowChatAgent.failedHint") }}</p>
        </article>
      </div>
    </div>
  </details>
  </div>
</template>

<script>
import * as api from "@/api/workflowChat";
import { WorkflowChatRealtimeClient } from "@/services/workflow-chat-realtime";
import { renderAgentServiceMarkdown } from "@/services/agent-service-markdown";

export default {
  name: "WorkflowChatAgentPanel",
  props: {
    mobile: { type: Boolean, default: false },
    active: { type: Boolean, default: true },
    canRequest: { type: Boolean, default: true },
    inline: { type: Boolean, default: false },
    group: { type: Object, required: true },
    currentUserId: { type: String, required: true },
  },
  data() {
    return {
      enabled: false,
      expanded: false,
      busy: false,
      error: "",
      tasks: [],
      client: null,
      epoch: 0,
      refreshNo: 0,
      attempts: {},
      capabilities: ["progress", "summary", "issues"],
    };
  },
  computed: {
    identity() {
      return `${this.$store.state.user?.userInfo?.token || ""}:${this.group.id}:${this.group.roundNo}`;
    },
  },
  watch: {
    active() { this.reset(); },
    identity() {
      this.reset();
    },
  },
  mounted() {
    this.reset();
  },
  beforeDestroy() {
    this.epoch++;
    this.client?.close();
  },
  methods: {
    // 身份/群/轮次改变先清空旧结果和订阅，迟到响应不能污染新窗口。
    async reset() {
      const epoch = ++this.epoch;
      this.client?.close();
      this.client = null;
      this.enabled = false;
      this.tasks = [];
      this.attempts = {};
      this.error = "";
      this.busy = false;
      if (this.active === false) return;
      try {
        const enabled = await api.workflowChatAgentAvailable(this.group.id);
        if (epoch !== this.epoch || !enabled) return;
        this.enabled = true;
        this.client = new WorkflowChatRealtimeClient({
          token: String(this.$store.state.user?.userInfo?.token || ""),
          onRefresh: () => (epoch === this.epoch ? this.refresh() : undefined),
          onError: () => {
            if (epoch === this.epoch && this.enabled)
              this.error = this.$t("workflowChatAgent.unavailable");
          },
          onEvent: (event) => {
            if (epoch !== this.epoch) return;
            if (
              event.resourceId === this.group.id &&
              ["resource.revoked", "resource.deleted"].includes(event.eventType)
            ) {
              this.enabled = false;
              this.tasks = [];
              this.client?.close();
            }
          },
        });
        this.client.connect();
        await this.refresh();
      } catch (error) {
        // 旧MTS或未开放能力不展示入口；其他错误保持可见，不伪造空的成功结果。
        if (
          epoch === this.epoch &&
          ![403, 404].includes(error.response?.status)
        )
          this.error = this.$t("workflowChatAgent.unavailable");
      }
    },
    async refresh() {
      if (!this.enabled) return;
      const epoch = this.epoch,
        serial = ++this.refreshNo,
        group = this.group;
      try {
        const result = await api.listWorkflowChatAgentTasks(
          group.id,
          group.roundNo,
        );
        if (epoch !== this.epoch || serial !== this.refreshNo || !this.enabled)
          return;
        // 列表只取引用；终态结果按版本复用，避免每条通知重新下载整群历史结果。
        const items = await Promise.all(
          (result.items || []).slice(0, 8).map(async (task) => {
            const cached = this.tasks.find(
              (old) =>
                old.taskId === task.taskId &&
                old.version === task.version &&
                old.result,
            );
            if (cached) return { ...task, result: cached.result };
            if (task.status === "SUCCEEDED" && !task.result)
              return api.getWorkflowChatAgentTask(group.id, task.taskId);
            return task;
          }),
        );
        if (epoch !== this.epoch || serial !== this.refreshNo || !this.enabled)
          return;
        this.tasks = items;
        this.error = "";
      } catch (error) {
        if (
          epoch === this.epoch &&
          serial === this.refreshNo &&
          [403, 404].includes(error.response?.status)
        ) {
          this.enabled = false;
          this.tasks = [];
          this.client?.close();
        }
        throw error;
      }
    },
    // 未收到创建回执时保留同一幂等键；模型只在显式点击后创建任务。
    async submit(capability) {
      if (this.busy || !this.enabled || this.active === false || this.canRequest === false || !this.capabilities.includes(capability)) return;
      const epoch = this.epoch,
        group = this.group;
      this.busy = true;
      this.error = "";
      const to = BigInt(group.lastSequence || 0);
      const request = this.attempts[capability] || {
        capability,
        fromSequence: String(to > 199n ? to - 199n : 0n),
        toSequence: String(to),
        workflowRound: group.roundNo,
        clientRequestId: crypto.randomUUID(),
        locale: this.$store.state.locale?.uiLocale || "zh-CN",
      };
      this.$set(this.attempts, capability, request);
      try {
        await api.createWorkflowChatAgentTask(group.id, request);
        if (epoch !== this.epoch) return;
        this.$delete(this.attempts, capability);
        await this.refresh();
      } catch (error) {
        if (epoch === this.epoch)
          this.error =
            error.message || this.$t("workflowChatAgent.unavailable");
      } finally {
        if (epoch === this.epoch) this.busy = false;
      }
    },
    canCancel(task) {
      return (
        String(task.requesterUserId) === this.currentUserId &&
        ["READY", "RUNNING", "WAITING_RETRY"].includes(task.status)
      );
    },
    async cancel(task) {
      if (this.busy || !this.canCancel(task)) return;
      const epoch = this.epoch;
      this.busy = true;
      try {
        await api.cancelWorkflowChatAgentTask(
          this.group.id,
          task.taskId,
          task.version,
        );
        if (epoch === this.epoch) await this.refresh();
      } catch (error) {
        if (epoch === this.epoch) this.error = error.message;
      } finally {
        if (epoch === this.epoch) this.busy = false;
      }
    },
    markdown(text) {
      return renderAgentServiceMarkdown(text);
    },
    evidenceLabel(id) {
      return id.startsWith("message:")
        ? `${this.$t("workflowChatAgent.messageEvidence")} #${id.slice(8)}`
        : this.$t("workflowChatAgent.businessEvidence");
    },
    evidence(task, id) {
      return (
        task.result?.sources?.find((source) => source.id === id)?.text ||
        this.$t("workflowChatAgent.evidenceMissing")
      );
    },
  },
};
</script>

<style scoped lang="less">
.workflow-agent-container { &.is-mobile { flex: 0 0 auto; min-width: 0; max-width: 100%; } }
.workflow-agent-inline {
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px 12px; color: #667085; font-size: 12px;
  button { min-height: 44px; border: 0; background: transparent; color: #246de0; }
  &.is-mobile {
    gap: 4px; padding: 4px 12px; background: #fff; border-bottom: 1px solid #eef2f7;
    .mobile-agent-actions { display: flex; flex: 0 0 100%; min-width: 0; gap: 8px; overflow-x: auto; button { flex: 1 0 auto; padding: 8px 12px; border: 1px solid #dde3ee; border-radius: 8px; white-space: nowrap; } }
  }
}
.workflow-agent {
  flex: 0 0 auto;
  min-width: 0;
  padding: 8px 12px;
  border-bottom: 1px solid #eef2f7;
  background: #fff;
  > summary {
    min-height: 44px;
    display: flex;
    align-items: center;
    color: #246de0;
    cursor: pointer;
    font-weight: 600;
  }
  &__body {
    min-width: 0;
  }
  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  button {
    min-height: 44px;
    padding: 8px 12px;
    border: 1px solid #dde3ee;
    border-radius: 8px;
    background: #fff;
    color: #334155;
    cursor: pointer;
    white-space: nowrap;
    &.primary {
      background: #246de0;
      border-color: #246de0;
      color: #fff;
    }
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }
  &__hint {
    color: #667085;
    font-size: 12px;
    margin: 8px 0;
  }
  &__tasks {
    max-height: 28vh;
    overflow-y: auto;
    overflow-wrap: anywhere;
    article {
      padding: 8px 0;
      border-top: 1px solid #eef2f7;
    }
    header {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
      font-size: 13px;
    }
    small {
      color: #667085;
    }
  }
  &__point {
    font-size: 14px;
    line-height: 1.6;
    margin-top: 8px;
  }
  &__evidence {
    color: #667085;
    font-size: 12px;
    summary {
      min-height: 44px;
      display: flex;
      align-items: center;
      cursor: pointer;
    }
    blockquote {
      margin: 0 0 8px;
      padding: 8px;
      background: #f5f7fb;
      white-space: pre-wrap;
    }
  }
}
</style>
